import { AnyAction } from 'redux'
import { getCountryFromUrl } from '../../routing/functions'

export enum SiteCountry {
  nl = 'nl',
  be = 'be',
  uk = 'uk',
  ie = 'ie',
  dk = 'dk',
}

export enum SiteLanguage {
  nl = 'nl',
  nlBe = 'nl-be',
  en = 'en',
  dk = 'dk',
}

export enum SiteBasicLanguage {
  nl = 'nl',
  en = 'en',
  dk = 'dk',
}

export enum SiteCurrency {
  euro = '€',
  pound = '£',
  krona = 'kr.',
}

export enum SiteCurrencyCode {
  EUR = 'EUR',
  GBP = 'GBP',
  DKK = 'DKK',
}

export const CountryLanguages = new Map([
  [SiteCountry.nl, SiteLanguage.nl],
  [SiteCountry.be, SiteLanguage.nlBe],
  [SiteCountry.uk, SiteLanguage.en],
  [SiteCountry.ie, SiteLanguage.en],
  [SiteCountry.dk, SiteLanguage.dk],
])

const CountryCurrencies = new Map([
  [SiteCountry.nl, SiteCurrency.euro],
  [SiteCountry.be, SiteCurrency.euro],
  [SiteCountry.uk, SiteCurrency.pound],
  [SiteCountry.ie, SiteCurrency.euro],
  [SiteCountry.dk, SiteCurrency.krona],
])

const CountryICC2s: PerCountry<string> = {
  [SiteCountry.nl]: 'NL',
  [SiteCountry.be]: 'BE',
  [SiteCountry.uk]: 'GB',
  [SiteCountry.ie]: 'IE',
  [SiteCountry.dk]: 'DK',
}

const initialCountry: SiteCountry = getCountryFromUrl(location.pathname)

export const displayedCountries = Object.values(SiteCountry)
export const displayedCurrencies = Object.values(SiteCurrency)
export const displayedCurrencyCodes = Object.values(SiteCurrencyCode)

export enum TransportTypes {
  airplane = 'airplane',
  train = 'train',
  car = 'car',
}

export type PerTransportType<T> = Per<TransportTypes, T>

export const countryTransportTypes: PerTransportType<SiteCountry[]> = {
  airplane: displayedCountries,
  car: [SiteCountry.nl, SiteCountry.be, SiteCountry.uk, SiteCountry.dk],
  train: [SiteCountry.nl],
}

export enum CrossSellTypes {
  co2 = 'co2',
}

export const countryCrossSells: Per<CrossSellTypes, SiteCountry[]> = {
  co2: [
    SiteCountry.nl,
    SiteCountry.be,
    SiteCountry.dk,
    SiteCountry.uk,
    SiteCountry.ie,
  ],
}

export interface Locale {
  country: SiteCountry
  userChangedCountry: boolean
  language: SiteLanguage
  currency: SiteCurrency
}

export const initialLocale: Locale = {
  country: initialCountry,
  userChangedCountry: false,
  language: CountryLanguages.get(initialCountry as SiteCountry)!,
  currency: CountryCurrencies.get(initialCountry as SiteCountry)!,
}

export const SET_COUNTRY_CODE = 'SET_COUNTRY_CODE'
export const SET_CURRENCY = 'SET_CURRENCY'

interface CountryAction {
  type: string
  country: SiteCountry
  userChange: boolean
}

interface CurrencyAction {
  type: string
  currency: SiteCurrency
}

export function setCountry(
  country: SiteCountry,
  userChange: boolean = false,
): CountryAction {
  return {
    type: SET_COUNTRY_CODE,
    country,
    userChange,
  }
}

export function setCurrency(currency: SiteCurrency): CurrencyAction {
  return {
    type: SET_CURRENCY,
    currency,
  }
}

export function localeReducer(state = initialLocale, action: AnyAction) {
  switch (action.type) {
    case SET_COUNTRY_CODE:
      return {
        country: action.country,
        userChangedCountry: action.userChange,
        language: CountryLanguages.get(action.country),
        currency: CountryCurrencies.get(action.country),
      }
    case SET_CURRENCY:
      return {
        country: state.country,
        language: state.language,
        currency: action.currency,
      }
    default:
      return state
  }
}

export const getSiteCountryICC2 = (siteCountry: SiteCountry) =>
  CountryICC2s[siteCountry]

export const getSiteCountryFromICC2 = (icc2: string): SiteCountry =>
  Object.entries(CountryICC2s)
    .map(([sc, code]) => ({ sc, code } as { sc: SiteCountry; code: string }))
    .find(({ code }) => code === icc2)?.sc || SiteCountry.uk

export function getSiteCurrencyISO(siteCurrency?: SiteCurrency): string {
  switch (siteCurrency) {
    case SiteCurrency.euro:
    default:
      return SiteCurrencyCode.EUR
    case SiteCurrency.pound:
      return SiteCurrencyCode.GBP
    case SiteCurrency.krona:
      return SiteCurrencyCode.DKK
  }
}

export function getSiteCurrencyFromAPI(
  currencyStr: SiteCurrencyCode,
): SiteCurrency {
  switch (currencyStr) {
    case SiteCurrencyCode.EUR:
    default:
      return SiteCurrency.euro
    case SiteCurrencyCode.GBP:
      return SiteCurrency.pound
    case SiteCurrencyCode.DKK:
      return SiteCurrency.krona
  }
}

export type Per<E extends string | number, T> = { [key in E]: T }
export type PerCountry<T> = Per<SiteCountry, T>
export type PerBasicLanguage<T> = Per<SiteBasicLanguage, T>

export function mapPerBasicLanguageToPerCountry<T>(
  perLang: PerBasicLanguage<T>,
): PerCountry<T> {
  const { nl, en, dk } = perLang
  return { nl, be: nl, uk: en, ie: en, dk }
}
